import 'leaflet/dist/leaflet.css';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { cooperplaceApi } from 'services/api';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import locationApi from 'services/apis/location';
import api from 'services/api';
import { Row, Col } from 'react-bootstrap';
import { FaBatteryQuarter, FaBatteryHalf, FaBatteryFull } from 'react-icons/fa';
import Text from 'v3/components/Text';
import { useSnackbar } from 'v3/components/Snackbar';
import { useTravel } from 'v3/pages/Travel';
import { formatDateTime, formatNumber } from 'v3/utils/formatter';
import theme from 'v3/theme';
import Progress from './Progress';
import StatusColor from './StatusColor';

import polyline from '@mapbox/polyline';
import 'leaflet-polylinedecorator';

import MarkerLetterMap from 'v3/components/Leaflet/MarkerLetterMap';
import MarkerMap from 'v3/components/Leaflet/Marker';
import MarkerTemperature from 'v3/components/Leaflet/MarkerTemperature';
import PolylineWithArrow from 'v3/components/Leaflet/Polyline';
import MarkerTollsPlaza from 'v3/components/Leaflet/MarkerTollsPlaza';
import MarkerTollsPlazaInfo from 'v3/components/Leaflet/MarkerTollsPlazaInfo';

import { MapContainer, Pane, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import Temperature from './Temperature';
import Alerts from '../Alerts';
import Speed from './Speed';
import 'leaflet-routing-machine';

const leafletRouteParams = {
  draggableWaypoints: false,
  routeWhileDragging: false,
  addWaypoints: false,
  containerClassName: 'd-none',
  createMarker: () => null,
  lineOptions: {
    styles: [{ color: '#0BB7AF', weight: 6, opacity: 0.8 }],
  },
};

const BRAZIL_CENTER = [-15.7801, -47.9292];

const getTollCoordinate = (toll, fieldNames = []) => {
  for (const fieldName of fieldNames) {
    const value = Number(toll?.[fieldName]);
    if (!Number.isNaN(value)) return value;
  }

  return null;
};

const getTollKey = (toll, index) => {
  const lat = getTollCoordinate(toll, ['lat', 'latitude']);
  const lng = getTollCoordinate(toll, ['long', 'lng', 'lon', 'longitude']);

  return toll?.codeAntt || `${lat}-${lng}-${index}`;
};

const getLocationCoordinate = (location, fieldNames = []) => {
  for (const fieldName of fieldNames) {
    const value = Number(location?.[fieldName]);
    if (!Number.isNaN(value)) return value;
  }

  return null;
};

const getFirstValue = (source, fieldNames = []) => {
  for (const fieldName of fieldNames) {
    const value = source?.[fieldName];

    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
};

const normalizeLocation = (location) => {
  const latitude = getLocationCoordinate(location, ['latitude', 'lat']);
  const longitude = getLocationCoordinate(location, [
    'longitude',
    'lng',
    'lon',
  ]);
  const battery = getFirstValue(location, [
    'battery_level',
    'batteryLevel',
    'battery',
  ]);
  const riskManagerId = getFirstValue(location, [
    'risk_manager_id',
    'riskManagerId',
  ]);
  const capturedAt = getFirstValue(location, [
    'captured_at',
    'capturedAt',
    'captureData',
    'datetime',
  ]);

  return {
    ...location,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    battery,
    battery_level: battery,
    risk_manager_id: riskManagerId,
    captured_at: capturedAt,
  };
};

const hasAppLocationSource = (location) =>
  location?.battery_level !== undefined &&
  location?.battery_level !== null &&
  location?.battery_level !== '' &&
  !location?.risk_manager_id;

export default function MapTravel() {
  const params = useParams();
  const travel = useTravel();
  const mapRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const snackbar = useSnackbar();
  const [locations, setLocations] = useState([]);
  const [map, setMap] = useState(null);
  const [coords, setCoords] = useState();
  const [latLngTemperature, setLatLngTemperature] = useState();
  const [tollsPlaza, setTollsPlaza] = useState();
  const [selectedTollKey, setSelectedTollKey] = useState();
  const [showTollPlaza, setShowTollPlaza] = useState(false);
  const [coordinatesPolyline, setCoordinatesPolyline] = useState([]);
  const [markersEnabled, setMarkersEnabled] = useState(true);
  const [trajectoryEnabled, setTrajectoryEnabled] = useState(true);
  const [tollsEnabled, setTollsEnabled] = useState(true);
  const [locationSources, setLocationSources] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [lastLocation, setLastLocation] = useState();
  const [tollsPlazaInfos, setTollsPlazaInfos] = useState();
  const [locationsFetched, setLocationsFetched] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));

  const getTollsPlazaByLoadId = async () => {
    try {
      const response = await cooperplaceApi.get(
        `tollsPlaza/by-load-id/${travel?.data?.loadId}`,
      );
      setTollsPlazaInfos(response?.data);
    } catch (error) {
      snackbar.show(
        <Text>
          {error?.response?.data?.message ||
            'Erro ao listar as praças de pedágio'}
        </Text>,
        {
          type: 'error',
        },
      );
    }
  };

  async function fetchAlert() {
    try {
      const response = await api.get(`travel/${params.id}/alerts`);
      setAlerts(response.data);
    } catch (error) {
    }
  }

  useEffect(() => {
    if (mapRef.current && !map) {
      setMap(mapRef.current);
    }

    if (params?.id) {
      fetchAlert();
    }

    if (travel?.data?.loadId) {
      getTollsPlazaByLoadId();
    }
  }, [params?.id, travel?.data?.loadId, map]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

      if (mapRef.current && isFullscreen) {
        mapRef.current.invalidateSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setIsFullscreen(fullscreenElement === mapWrapperRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.invalidateSize();

    const id = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 250);

    return () => clearTimeout(id);
  }, [isFullscreen]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;

    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  const { origin, destinations } = useMemo(
    () => ({
      origin: travel.data?.loads?.loadOrigins?.[0],
      destinations: travel.data?.loads?.loadDestinations,
    }),
    [travel.data],
  );

  const middleDestinations = useMemo(
    () => destinations?.filter((_, index) => index < destinations?.length - 1),
    [destinations],
  );
  const lastDestination = useMemo(
    () => destinations?.[destinations?.length - 1],
    [destinations],
  );
  const getPointLetter = (index) => String.fromCharCode(65 + index);
  const initialMapCenter = useMemo(() => {
    const originLat = getLocationCoordinate(origin, ['lat', 'latitude']);
    const originLng = getLocationCoordinate(origin, ['lng', 'lon', 'longitude']);

    if (originLat !== null && originLng !== null) {
      return [originLat, originLng];
    }

    return BRAZIL_CENTER;
  }, [origin]);

  useEffect(() => {
    async function fetchTravelLocations() {
      try {
        const originLoad = travel.data?.loads?.loadOrigins?.[0];
        const lastDestinationLoad =
          travel.data?.loads?.loadDestinations?.[
            travel.data.loads?.loadDestinations?.length - 1
          ];

        const response = await locationApi.get(
          `range/cpf/${travel.data?.driver?.cpfCnpj}`,
          {
            params: {
              start: originLoad?.scheduledTime,
              end: moment(lastDestinationLoad?.scheduledTime)
                .add(3, 'days')
                .toDate(),
              attended_id: travel.data.id,
              all_sources: true,
            },
          },
        );

        const locationsFiltered = response.data.locations
          .map(normalizeLocation)
          .filter((item) => {
            const isValidSpeed = item.speed >= 0;
            if (locationSources.risk_manager_id) {
              return (
                isValidSpeed &&
                String(item.risk_manager_id) ===
                  String(locationSources.risk_manager_id)
              );
            }

            return isValidSpeed;
          });

        setLocations(locationsFiltered);
        setLastLocation(locationsFiltered.slice(-1)[0]);
        setLocationsFetched(true);
      } catch (error) {
        setLocationsFetched(true);
      }
    }

    if (!travel.isLoading && travel.data.id && !locationsFetched) {
      fetchTravelLocations();
    }
  }, [
    travel.isLoading,
    travel.data.id,
    travel.data?.driver,
    travel.data?.loads?.loadOrigins,
    travel.data?.loads?.loadDestinations,
    locationSources,
    locationsFetched,
    snackbar,
  ]);

  useEffect(() => {

    const loadPolyline =
      travel?.data?.loads?.polylineRoute ||
      travel?.data?.loads?.polyline_route ||
      travel?.data?.loads?.polilyneRoute ||
      travel?.data?.loads?.polilyne_route ||
      travel?.data?.polylineRoute ||
      travel?.data?.polyline_route ||
      travel?.data?.polilyneRoute ||
      travel?.data?.polilyne_route ||
      travel?.data?.tracking?.polyline ||
      null;

    if (!loadPolyline) {
      setCoordinatesPolyline([]);
      return;
    }

    try {
      const decoded = polyline.decode(loadPolyline);
      setCoordinatesPolyline(decoded);
    } catch (err) {
      setCoordinatesPolyline([]);
    }
  }, [
    travel?.data?.loads?.polylineRoute,
    travel?.data?.loads?.polyline_route,
    travel?.data?.loads?.polilyneRoute,
    travel?.data?.loads?.polilyne_route,
    travel?.data?.polylineRoute,
    travel?.data?.polyline_route,
    travel?.data?.polilyneRoute,
    travel?.data?.polilyne_route,
    travel?.data?.tracking?.polyline,
  ]);

  useEffect(() => {
    if (!map) return;

    const points = [];

    if (coordinatesPolyline?.length > 0) {
      coordinatesPolyline.forEach(([lat, lng]) => {
        points.push([lat, lng]);
      });
    } else if (locations?.length > 0) {

      locations.forEach((l) => {
        if (l.lat && l.lng) points.push([l.lat, l.lng]);
      });
    }

    if (origin?.lat && origin?.lng) {
      points.push([origin.lat, origin.lng]);
    }
    destinations?.forEach((d) => {
      if (d?.lat && d?.lng) points.push([d.lat, d.lng]);
    });

    if (!points.length) return;

    const bounds = L.latLngBounds(points);

    if (!bounds.isValid()) return;

    map.fitBounds(bounds);
  }, [map, origin, destinations, locations, coordinatesPolyline]);

  if (travel.isLoading) {
    return null;
  }

  function handleClickVelocity(data) {
    const leafletMap = map || mapRef.current;
    const lat = getLocationCoordinate(data, ['latitude', 'lat']);
    const lng = getLocationCoordinate(data, ['longitude', 'lng', 'lon']);

    if (lat !== null && lng !== null && leafletMap) {
      leafletMap.setView([lat, lng], 15, {
        animate: true,
      });
    }
  }

  function handleClickTemperature(data) {
    const leafletMap = map || mapRef.current;
    const lat = getLocationCoordinate(data, ['latitude', 'lat']);
    const lng = getLocationCoordinate(data, ['longitude', 'lng', 'lon']);

    if (lat !== null && lng !== null && leafletMap) {
      leafletMap.setView([lat, lng], 15, {
        animate: true,
      });
    }
  }

  function handleClickTollsPlaza(data, index, markerMap) {
    const tollKey = getTollKey(data, index);

    if (tollKey === selectedTollKey && showTollPlaza) {
      setShowTollPlaza(false);
      setSelectedTollKey();
      return;
    }

    const lat = getTollCoordinate(data, ['lat', 'latitude']);
    const lng = getTollCoordinate(data, ['long', 'lng', 'lon', 'longitude']);

    if (lat === null || lng === null) return;

    setTollsPlaza(data);
    setSelectedTollKey(tollKey);
    setShowTollPlaza(true);

    const leafletMap = markerMap || map;

    if (leafletMap) {
      leafletMap.setView([lat, lng], 15);
    }
  }

  const controlButtonStyle = {
    width: 34,
    height: 34,
    borderRadius: 6,
    border: '1px solid #d0d0d0',
    background: '#ffffff',
    color: '#333333',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
  };

  const toggleFullscreen = useCallback(() => {
    const element = mapWrapperRef.current;

    if (!element || typeof document === 'undefined') {
      setIsFullscreen((prev) => !prev);
      return;
    }

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fullscreenElement) {
      const exitFullscreen =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

      if (exitFullscreen) {
        exitFullscreen.call(document);
        return;
      }
    }

    const requestFullscreen =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;

    if (requestFullscreen) {
      requestFullscreen.call(element).catch?.(() => {
        setIsFullscreen((prev) => !prev);
      });
      return;
    }

    setIsFullscreen((prev) => !prev);
  }, []);

  const mapWrapperStyle = {
    position: isFullscreen ? 'fixed' : 'relative',
    top: isFullscreen ? 0 : 'auto',
    left: isFullscreen ? 0 : 'auto',
    right: isFullscreen ? 0 : 'auto',
    bottom: isFullscreen ? 0 : 'auto',
    zIndex: isFullscreen ? 9999 : 0,
    height: isFullscreen ? '100vh' : '850px',
    width: isFullscreen ? '100vw' : '100%',
    background: '#ffffff',
    borderRadius: isFullscreen ? 0 : 8,
    overflow: 'hidden',
  };

  return (
    <>
      <Row>
        <Col>
          <div ref={mapWrapperRef} style={mapWrapperStyle}>
            <div
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1100,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <button
                type="button"
                onClick={() => mapRef.current?.zoomIn()}
                style={controlButtonStyle}
                aria-label="Aumentar zoom"
                title="Aumentar zoom"
              >
                +
              </button>

              <button
                type="button"
                onClick={() => mapRef.current?.zoomOut()}
                style={controlButtonStyle}
                aria-label="Diminuir zoom"
                title="Diminuir zoom"
              >
                -
              </button>

              <button
                type="button"
                onClick={toggleFullscreen}
                style={controlButtonStyle}
                aria-label={
                  isFullscreen ? 'Sair da tela cheia' : 'Ir para tela cheia'
                }
                title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              >
                {isFullscreen ? '⤫' : '⛶'}
              </button>
            </div>

            <MapContainer
              key={`${isFullscreen}-${viewportSize.width}x${viewportSize.height}`}
              ref={mapRef}
              center={initialMapCenter}
              zoom={6}
              zoomControl={false}
              style={{ height: '100%', width: '100%', zIndex: 0 }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              {origin?.lat && origin?.lng && markersEnabled && (
                <MarkerLetterMap
                  position={[origin.lat, origin.lng]}
                  background="#0bb7af"
                  letter={getPointLetter(0)}
                  color="#fff"
                />
              )}

              {middleDestinations &&
                markersEnabled &&
                middleDestinations.map((dest, index) => {
                  if (dest?.lat && dest?.lng) {
                    return (
                      <MarkerLetterMap
                        key={`mid-${dest.id || `${dest.lat}-${dest.lng}`}`}
                        position={[dest?.lat, dest?.lng]}
                        background={theme.colors.light_blue}
                        letter={getPointLetter(index + 1)}
                        color="#fff"
                      />
                    );
                  }
                  return null;
                })}

              {lastDestination?.lat &&
                lastDestination?.lng &&
                markersEnabled && (
                  <MarkerLetterMap
                    background="#F64E60"
                    position={[
                      lastDestination.lat,
                      lastDestination.lng,
                    ]}
                    letter={getPointLetter(destinations.length)}
                    color="#fff"
                  />
                )}

              {lastLocation?.latitude &&
              lastLocation?.longitude &&
              lastLocation?.captured_at &&
              moment().diff(moment(lastLocation.captured_at), 'hours') <
                24 ? (
                <MarkerLetterMap
                  background={
                    hasAppLocationSource(lastLocation)
                      ? 'app.svg'
                      : 'current.svg'
                  }
                  sizeIcon={30}
                  position={[
                    lastLocation?.latitude,
                    lastLocation?.longitude,
                  ]}
                  color="#fff"
                  title="A localização do veículo dentro das 24 horas"
                />
              ) : lastLocation?.latitude &&
                lastLocation?.longitude &&
                lastLocation?.captured_at &&
                moment().diff(
                  moment(lastLocation.captured_at),
                  'hours',
                ) > 24 ? (
                <MarkerLetterMap
                  background={
                    hasAppLocationSource(lastLocation)
                      ? 'app_late.svg'
                      : 'current_late.svg'
                  }
                  sizeIcon={30}
                  position={[
                    lastLocation?.latitude,
                    lastLocation?.longitude,
                  ]}
                  color="#fff"
                  title="A localização do veículo excedeu as 24 horas"
                  style={{ cursor: 'help' }}
                />
              ) : null}

              {markersEnabled &&
                coords?.latitude &&
                coords?.longitude && (
                  <MarkerMap
                    position={[coords?.latitude, coords?.longitude]}
                    background="#7337EE"
                    onClose={() => setCoords()}
                    text={
                      <>
                        <div>
                          <Text color="#fff" type="label">
                            {formatDateTime(coords.captured_at)}
                          </Text>
                        </div>
                        <div>
                          <Text color="#fff" type="label">
                            {formatNumber(coords.velocidade) || 0}{' '}
                            km/h
                          </Text>
                        </div>
                        <div>
                          <Text color="#fff" type="label">
                            {coords.battery}%
                            {coords.battery > 75 && (
                              <FaBatteryFull
                                size={16}
                                style={{
                                  marginLeft: 5,
                                  marginBottom: 3,
                                }}
                              />
                            )}
                            {coords.battery >= 50 &&
                              coords.battery <= 75 && (
                                <FaBatteryHalf
                                  size={16}
                                  style={{
                                    marginLeft: 5,
                                    marginBottom: 3,
                                  }}
                                />
                              )}
                            {coords.battery >= 0 &&
                              coords.battery < 50 && (
                                <FaBatteryQuarter
                                  size={16}
                                  style={{
                                    marginLeft: 5,
                                    marginBottom: 3,
                                  }}
                                />
                              )}
                          </Text>
                        </div>
                      </>
                    }
                  />
                )}

              {markersEnabled &&
                latLngTemperature?.latitude &&
                latLngTemperature?.longitude && (
                  <MarkerTemperature
                    position={[
                      latLngTemperature?.latitude,
                      latLngTemperature?.longitude,
                    ]}
                    background="#7337EE"
                    paramsTemperature={latLngTemperature}
                    onClose={() => setLatLngTemperature()}
                  />
                )}

              <>
                {trajectoryEnabled && locations?.length > 0 && (
                  <PolylineWithArrow
                    locations={locations}
                    mapRef={mapRef}
                    polylineColor="#fbad26"
                  />
                )}
                {trajectoryEnabled && coordinatesPolyline?.length > 0 && (
                  <PolylineWithArrow
                    locations={coordinatesPolyline}
                    mapRef={mapRef}
                    polylineColor="#0bb7af"
                  />
                )}
              </>

              <Pane
                name="tollPlazaPane"
                style={{ zIndex: 650, pointerEvents: 'auto' }}
              >
                {tollsEnabled &&
                  tollsPlazaInfos?.tollsPlaza?.map((item, index) => {
                    const lat = getTollCoordinate(item, ['lat', 'latitude']);
                    const lng = getTollCoordinate(item, [
                      'long',
                      'lng',
                      'lon',
                      'longitude',
                    ]);

                    if (lat === null || lng === null) return null;

                    return (
                      <MarkerTollsPlaza
                        key={`toll-${item?.codeAntt || 'no-code'}-${lat}-${lng}-${index}`}
                        position={[lat, lng]}
                        background="#d32f2f"
                        toll={item}
                        index={index}
                        handleClickTollsPlaza={handleClickTollsPlaza}
                      />
                    );
                  })}

                {tollsEnabled && showTollPlaza && (
                  <MarkerTollsPlazaInfo
                    toll={tollsPlaza}
                    onClose={() => {
                      setShowTollPlaza(false);
                      setSelectedTollKey();
                    }}
                  />
                )}
              </Pane>
            </MapContainer>

            <Progress
              isFullscreen={isFullscreen}
              markers={(value) => setMarkersEnabled(value)}
              path={(value) => setTrajectoryEnabled(value)}
              tolls={(value) => {
                setTollsEnabled(value);
                if (!value) {
                  setShowTollPlaza(false);
                }
              }}
              onChangeSource={(value) => {
                setLocationSources(value);
                setLocationsFetched(false);
              }}
            />
            <StatusColor />
          </div>

          <div style={{ marginTop: 50 }}>
            {locations?.length < 1 ||
            locations.every(({ speed }) => !speed) ? (
              <Col className="d-flex justify-content-center align-items-center mt-3">
                <Text color="gray">
                  Não temos dados sobre a velocidade
                </Text>
              </Col>
            ) : (
              <Speed
                latLong={locations}
                setCoords={setCoords}
                handleClickVelocity={handleClickVelocity}
              />
            )}
          </div>

          {travel?.data?.loadTemperature?.length > 0 && (
            <Temperature
              handleClickTemperature={handleClickTemperature}
              setLatLngTemperature={setLatLngTemperature}
            />
          )}
          <Alerts alerts={alerts} />
        </Col>
      </Row>
    </>
  );
}
